import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Pencil, Trash2, Plus } from "lucide-react";
import { api, formatApiError, eur } from "../../lib/api";

const TABS = [
  ["panoramica", "Panoramica"],
  ["colori", "Colori"],
  ["centri", "Centri"],
  ["prenotazioni", "Prenotazioni"],
  ["recessi", "Recessi"],
  ["finanza", "Finanza"],
  ["hospitality", "Hospitality"],
  ["testdrive", "Test Drive"],
  ["guide", "Guide"],
  ["termini", "Termini"],
];

const STOCKS = [
  ["available", "Disponibile"],
  ["low", "Scorte limitate"],
  ["unavailable", "Non disponibile"],
  ["incoming", "In arrivo"],
  ["hidden", "Nascosto"],
];

const ROLES = [["test_drive", "Test Drive"], ["resell", "Vendita"], ["service", "Service"]];

const inputCls = "w-full border border-white/15 bg-ink px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-swapa-yellow focus:outline-none";

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      <input {...props} className={inputCls} />
    </label>
  );
}

function ColoursTab() {
  const [items, setItems] = useState([]);
  const blank = { slug: "", name: "", hex: "#111111", image: "", stock: "available", order: 0 };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/colours").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/colours/${editing}`, form);
      else await api.post("/admin/colours", form);
      toast.success("Colore salvato");
      setForm(blank);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <ul className="space-y-3" data-testid="admin-colours-list">
        {items.map((c) => (
          <li key={c.slug} className="flex items-center justify-between border border-white/10 p-4">
            <span className="flex items-center gap-3">
              <span className="h-8 w-8 rounded-full border border-white/20" style={{ backgroundColor: c.hex }} />
              <span>
                <span className="block text-sm font-semibold text-white">{c.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{c.slug} · {STOCKS.find(([k]) => k === c.stock)?.[1]}</span>
              </span>
            </span>
            <span className="flex gap-2">
              <button data-testid={`admin-colour-edit-${c.slug}`} onClick={() => { setEditing(c.slug); setForm({ slug: c.slug, name: c.name, hex: c.hex, image: c.image || "", stock: c.stock, order: c.order }); }} className="border border-white/15 p-2 text-zinc-400 hover:text-white"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`admin-colour-delete-${c.slug}`} onClick={async () => { await api.delete(`/admin/colours/${c.slug}`); toast.success("Eliminato"); load(); }} className="border border-white/15 p-2 text-zinc-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </span>
          </li>
        ))}
      </ul>
      <div className="border border-white/10 bg-card p-6" data-testid="admin-colour-form">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{editing ? `Modifica: ${editing}` : "Nuovo colore"}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="Slug" data-testid="admin-colour-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="nero-vulcano" />
          <Field label="Nome" data-testid="admin-colour-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nero Vulcano" />
          <Field label="Hex" data-testid="admin-colour-hex" type="color" value={form.hex} onChange={(e) => setForm({ ...form, hex: e.target.value })} />
          <Field label="Ordine" data-testid="admin-colour-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />
          <label className="block sm:col-span-2">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Disponibilità stock</span>
            <select data-testid="admin-colour-stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputCls}>
              {STOCKS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">URL immagine veicolo (asset del cliente)</span>
            <input data-testid="admin-colour-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" className={inputCls} />
          </label>
        </div>
        <div className="mt-5 flex gap-3">
          <button data-testid="admin-colour-save" onClick={save} className="bg-swapa-yellow px-5 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-black">
            {editing ? "Salva modifiche" : "Crea colore"}
          </button>
          {editing && <button onClick={() => { setEditing(null); setForm(blank); }} className="border border-white/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Annulla</button>}
        </div>
      </div>
    </div>
  );
}

function CentresTab() {
  const [items, setItems] = useState([]);
  const blank = { name: "", city: "", address: "", cap: "", province: "", host: "", lat: 41.9, lng: 12.5, phone: "", email: "", hours: "", roles: [] };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/centres").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleRole = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/centres/${editing}`, form);
      else await api.post("/admin/centres", form);
      toast.success("Centro salvato");
      setForm(blank);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <ul className="space-y-3" data-testid="admin-centres-list">
        {items.map((c) => (
          <li key={c.id} className="flex items-center justify-between border border-white/10 p-4">
            <span>
              <span className="block text-sm font-semibold text-white">{c.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{c.city}{c.host ? ` · presso ${c.host}` : ""} · {c.roles.join(", ")}</span>
            </span>
            <span className="flex gap-2">
              <button data-testid={`admin-centre-edit-${c.id}`} onClick={() => { setEditing(c.id); setForm({ name: c.name, city: c.city, address: c.address, cap: c.cap, province: c.province, host: c.host || "", lat: c.lat, lng: c.lng, phone: c.phone, email: c.email, hours: c.hours, roles: c.roles }); }} className="border border-white/15 p-2 text-zinc-400 hover:text-white"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`admin-centre-delete-${c.id}`} onClick={async () => { await api.delete(`/admin/centres/${c.id}`); toast.success("Eliminato"); load(); }} className="border border-white/15 p-2 text-zinc-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </span>
          </li>
        ))}
      </ul>
      <div className="border border-white/10 bg-card p-6" data-testid="admin-centre-form">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{editing ? "Modifica centro" : "Nuovo centro"}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="Nome" data-testid="admin-centre-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field label="Ragione sociale (presso)" data-testid="admin-centre-host" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} />
          <Field label="Città" data-testid="admin-centre-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <Field label="Indirizzo" data-testid="admin-centre-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <Field label="CAP" data-testid="admin-centre-cap" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} />
          <Field label="Provincia" data-testid="admin-centre-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} />
          <Field label="Orari" data-testid="admin-centre-hours" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} />
          <Field label="Latitudine" data-testid="admin-centre-lat" type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: Number(e.target.value) })} />
          <Field label="Longitudine" data-testid="admin-centre-lng" type="number" step="0.0001" value={form.lng} onChange={(e) => setForm({ ...form, lng: Number(e.target.value) })} />
          <Field label="Telefono" data-testid="admin-centre-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Field label="Email" data-testid="admin-centre-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="mt-4 flex gap-3">
          {ROLES.map(([k, l]) => (
            <label key={k} className="flex cursor-pointer items-center gap-2 text-xs text-zinc-400">
              <input type="checkbox" data-testid={`admin-centre-role-${k}`} checked={form.roles.includes(k)} onChange={() => toggleRole(k)} className="h-4 w-4 accent-[#FFD700]" />
              {l}
            </label>
          ))}
        </div>
        <div className="mt-5 flex gap-3">
          <button data-testid="admin-centre-save" onClick={save} className="bg-swapa-yellow px-5 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-black">
            {editing ? "Salva modifiche" : "Crea centro"}
          </button>
          {editing && <button onClick={() => { setEditing(null); setForm(blank); }} className="border border-white/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Annulla</button>}
        </div>
      </div>
    </div>
  );
}

function ReservationsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/reservations").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="overflow-x-auto" data-testid="admin-reservations-table">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
            <th className="py-3 pr-4">ID</th><th className="pr-4">Cliente</th><th className="pr-4">Colore</th><th className="pr-4">Contatti</th><th className="pr-4">Pagamento</th><th>Data</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.reservation_id} className="border-b border-white/5">
              <td className="py-3 pr-4 font-mono text-swapa-yellow">{r.reservation_id}</td>
              <td className="pr-4 text-white">{r.first_name} {r.last_name}</td>
              <td className="pr-4 text-zinc-400">{r.colour_name}</td>
              <td className="pr-4 text-zinc-400">{r.email}<br />{r.phone}</td>
              <td className="pr-4">
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] ${r.payment_status === "paid" ? "text-emerald-400" : "text-zinc-500"}`}>
                  {r.payment_status === "paid" ? `Pagato ${eur(r.deposit)}` : r.status}
                </span>
              </td>
              <td className="text-zinc-500">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-zinc-600">Nessuna prenotazione ancora.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function FinanceTab() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/finance").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  if (!cfg) return <p className="font-mono text-xs text-zinc-500">Caricamento…</p>;

  const setOffer = (idx, k, v) => {
    const offers = cfg.offers.map((o, i) => (i === idx ? { ...o, [k]: v } : o));
    setCfg({ ...cfg, offers });
  };

  const save = async () => {
    try {
      const payload = { ...cfg, offers: cfg.offers.map(({ legal_status, ...o }) => ({ ...o, rate: Number(o.rate), interval: undefined })) };
      await api.put("/admin/finance", { offers: payload.offers, interval_first: Number(cfg.interval_first), interval_second: Number(cfg.interval_second) });
      toast.success("Configurazione finanza salvata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const F = (idx, key, label, testid, type = "text") => (
    <label className="block">
      <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      <input type={type} data-testid={testid} value={cfg.offers[idx][key] ?? ""} onChange={(e) => setOffer(idx, key, e.target.value)} className={inputCls} />
    </label>
  );

  return (
    <div data-testid="admin-finance-form">
      <div className="flex flex-wrap items-end gap-6 border border-white/10 bg-card p-5">
        <label className="block">
          <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Primo popup dopo (secondi)</span>
          <input type="number" data-testid="admin-finance-interval-first" value={cfg.interval_first} onChange={(e) => setCfg({ ...cfg, interval_first: e.target.value })} className={`${inputCls} w-28`} />
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Secondo popup dopo (secondi)</span>
          <input type="number" data-testid="admin-finance-interval-second" value={cfg.interval_second} onChange={(e) => setCfg({ ...cfg, interval_second: e.target.value })} className={`${inputCls} w-28`} />
        </label>
        <button data-testid="admin-finance-save" onClick={save} className="bg-swapa-yellow px-6 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-black">
          Salva tutto
        </button>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        {cfg.offers.map((o, idx) => (
          <div key={o.id} className="border border-white/10 bg-card p-6" data-testid={`admin-finance-offer-${o.id}`}>
            <div className="flex items-center justify-between">
              <p className="font-display text-lg font-bold uppercase text-white">Offerta {o.id === "a" ? "A — con anticipo" : "B — anticipo zero"}</p>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-zinc-400">
                <input type="checkbox" data-testid={`admin-finance-active-${o.id}`} checked={o.active} onChange={(e) => setOffer(idx, "active", e.target.checked)} className="h-4 w-4 accent-[#FFD700]" />
                Attiva
              </label>
            </div>
            {o.legal_status === "LEGAL DISCLOSURE REQUIRED" && (
              <p data-testid={`admin-finance-legal-warning-${o.id}`} className="mt-3 border border-swapa-yellow/50 bg-swapa-yellow/10 px-3 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-swapa-yellow">
                LEGAL DISCLOSURE REQUIRED — inserisci l'informativa di trasparenza completa
              </p>
            )}
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {F(idx, "monthly", "Rata mensile (€)", `admin-finance-monthly-${o.id}`)}
              {F(idx, "anticipo", "Anticipo / primo canone (€)", `admin-finance-anticipo-${o.id}`)}
              {F(idx, "anticipo_label", "Messaggio anticipo", `admin-finance-anticipolabel-${o.id}`)}
              {F(idx, "rate", "Numero di rate", `admin-finance-rate-${o.id}`, "number")}
              {F(idx, "tan", "TAN", `admin-finance-tan-${o.id}`)}
              {F(idx, "taeg", "TAEG", `admin-finance-taeg-${o.id}`)}
              {F(idx, "rata_finale", "Rata finale / maxirata (€)", `admin-finance-ratafinale-${o.id}`)}
              {F(idx, "refi", "Opzione rifinanziamento", `admin-finance-refi-${o.id}`)}
              {F(idx, "cta", "Testo CTA principale", `admin-finance-cta-${o.id}`)}
              {F(idx, "image", "URL immagine veicolo", `admin-finance-image-${o.id}`)}
              {F(idx, "campaign_start", "Inizio campagna (AAAA-MM-GG)", `admin-finance-start-${o.id}`)}
              {F(idx, "campaign_end", "Fine campagna (AAAA-MM-GG)", `admin-finance-end-${o.id}`)}
            </div>
            <label className="mt-4 block">
              <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                Informativa legale completa (importo totale del credito, totale dovuto, spese, finanziatore, esempio rappresentativo, validità offerta)
              </span>
              <textarea rows={5} data-testid={`admin-finance-legal-${o.id}`} value={o.legal} onChange={(e) => setOffer(idx, "legal", e.target.value)} className={`${inputCls} text-xs`} />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

function WithdrawalsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/withdrawals").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="overflow-x-auto" data-testid="admin-withdrawals-table">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead>
          <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
            <th className="py-3 pr-4">Prenotazione</th><th className="pr-4">Email</th><th className="pr-4">Motivo</th><th>Data</th>
          </tr>
        </thead>
        <tbody>
          {items.map((w) => (
            <tr key={w.id} className="border-b border-white/5">
              <td className="py-3 pr-4 font-mono text-swapa-yellow">{w.reservation_id}</td>
              <td className="pr-4 text-zinc-400">{w.email}</td>
              <td className="pr-4 text-zinc-500">{w.reason || "—"}</td>
              <td className="text-zinc-500">{new Date(w.created_at).toLocaleDateString("it-IT")}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-zinc-600">Nessuna richiesta di recesso.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function LeadsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/leads").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="overflow-x-auto" data-testid="admin-leads-table">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead>
          <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
            <th className="py-3 pr-4">Nome</th><th className="pr-4">Contatti</th><th className="pr-4">Zona</th><th className="pr-4">Messaggio</th><th>Data</th>
          </tr>
        </thead>
        <tbody>
          {items.map((l) => (
            <tr key={l.id} className="border-b border-white/5">
              <td className="py-3 pr-4 text-white">{l.name}</td>
              <td className="pr-4 text-zinc-400">{l.email}<br />{l.phone}</td>
              <td className="pr-4 text-zinc-400">{l.cap} {l.city}</td>
              <td className="pr-4 text-zinc-500">{l.message}</td>
              <td className="text-zinc-500">{new Date(l.created_at).toLocaleDateString("it-IT")}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-zinc-600">Nessuna richiesta test drive ancora.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function HospitalityTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/hospitality-leads").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const download = async (fileId, name) => {
    try {
      const r = await api.get(`/admin/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "allegato";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download non riuscito");
    }
  };
  return (
    <div className="overflow-x-auto" data-testid="admin-hospitality-table">
      <table className="w-full min-w-[900px] text-left text-sm">
        <thead>
          <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
            <th className="py-3 pr-4">Data</th><th className="pr-4">Struttura</th><th className="pr-4">Contatto</th><th className="pr-4">Tipo</th><th className="pr-4">Flotta</th><th className="pr-4">Usi</th><th className="pr-4">Allegati</th><th className="pr-4">UTM</th><th>Messaggio</th>
          </tr>
        </thead>
        <tbody>
          {items.map((l) => (
            <tr key={l.id} className="border-b border-white/5 align-top">
              <td className="py-3 pr-4 text-zinc-500">{new Date(l.created_at).toLocaleDateString("it-IT")}</td>
              <td className="pr-4 text-white">{l.company}<br /><span className="text-zinc-500">{l.city} {l.country}</span></td>
              <td className="pr-4 text-zinc-400">{l.name}{l.role ? ` (${l.role})` : ""}<br />{l.email}<br />{l.phone}{l.website ? (<><br /><span className="text-zinc-500">{l.website}</span></>) : null}</td>
              <td className="pr-4 text-zinc-400">{l.property_type}</td>
              <td className="pr-4 text-zinc-400">{l.fleet_size}</td>
              <td className="pr-4 text-zinc-400">{(l.intended_uses || []).join(", ")}</td>
              <td className="pr-4">
                {(l.files || []).length === 0 && <span className="text-zinc-600">—</span>}
                {(l.files || []).map((f) => (
                  <button key={f.id} onClick={() => download(f.id, f.original_filename)} data-testid={`hospitality-file-${f.field}`}
                    className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-swapa-yellow underline-offset-4 hover:underline">
                    {f.field === "logo" ? "Logo" : "Linee guida"}
                  </button>
                ))}
              </td>
              <td className="pr-4 text-zinc-500">{[l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(" / ") || "—"}</td>
              <td className="text-zinc-500">{l.message}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-zinc-600">Nessun lead hospitality ancora.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function GuidesTab() {
  const [items, setItems] = useState([]);
  const blank = { slug: "", title: "", meta_description: "", category: "microcar", excerpt: "", hero_image: "", reading_time: 5, keywords: [], body: "", published: true, direct_answer: "", faq: [], sources: [], related: [], pillar: false, author: "Redazione SWAPA", updated_at: "", cta_level: "medium" };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/guides").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const parseLines = (v, sep) => (typeof v === "string" ? v.split("\n").map((l) => l.split(sep).map((x) => x.trim())).filter((p) => p[0]) : v) || [];
    const payload = {
      ...form,
      keywords: typeof form.keywords === "string" ? form.keywords.split(",").map((k) => k.trim()).filter(Boolean) : form.keywords,
      related: typeof form.related === "string" ? form.related.split(",").map((k) => k.trim()).filter(Boolean) : form.related,
      faq: typeof form.faq === "string" ? parseLines(form.faq, "|").filter((p) => p.length >= 2).map((p) => ({ q: p[0], a: p.slice(1).join(" | ") })) : form.faq,
      sources: typeof form.sources === "string" ? parseLines(form.sources, "|").map((p) => ({ label: p[0], url: p[1] || "" })) : form.sources,
      reading_time: Number(form.reading_time),
    };
    try {
      if (editing) await api.put(`/admin/guides/${editing}`, payload);
      else await api.post("/admin/guides", payload);
      toast.success("Guida salvata");
      setForm(blank);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1.4fr]">
      <ul className="space-y-3" data-testid="admin-guides-list">
        {items.map((g) => (
          <li key={g.slug} className="flex items-center justify-between border border-white/10 p-4">
            <span>
              <span className="block text-sm font-semibold text-white">{g.title}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{g.slug} {g.published ? "" : "· bozza"}</span>
            </span>
            <span className="flex gap-2">
              <button data-testid={`admin-guide-edit-${g.slug}`} onClick={() => { setEditing(g.slug); setForm({ ...blank, ...g, keywords: (g.keywords || []).join(", "), related: (g.related || []).join(", "), faq: (g.faq || []).map((f) => `${f.q} | ${f.a}`).join("\n"), sources: (g.sources || []).map((s) => `${s.label} | ${s.url || ""}`).join("\n") }); }} className="border border-white/15 p-2 text-zinc-400 hover:text-white"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`admin-guide-delete-${g.slug}`} onClick={async () => { await api.delete(`/admin/guides/${g.slug}`); toast.success("Eliminata"); load(); }} className="border border-white/15 p-2 text-zinc-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </span>
          </li>
        ))}
      </ul>
      <div className="border border-white/10 bg-card p-6" data-testid="admin-guide-form">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{editing ? `Modifica: ${editing}` : "Nuova guida"}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="Slug" data-testid="admin-guide-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="microcar-elettrica-…" />
          <Field label="Titolo" data-testid="admin-guide-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Field label="Categoria" data-testid="admin-guide-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <Field label="Tempo lettura (min)" data-testid="admin-guide-reading" type="number" value={form.reading_time} onChange={(e) => setForm({ ...form, reading_time: e.target.value })} />
          <label className="block sm:col-span-2">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Meta description (SEO)</span>
            <input data-testid="admin-guide-meta" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} className={inputCls} />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Keywords (separate da virgola)</span>
            <input data-testid="admin-guide-keywords" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className={inputCls} />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Estratto</span>
            <textarea data-testid="admin-guide-excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={inputCls} />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">URL immagine hero</span>
            <input data-testid="admin-guide-hero" value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} className={inputCls} />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Corpo (## sottotitoli, ### sotto-sottotitoli, [testo](/link), - elenchi, riga vuota tra paragrafi)</span>
            <textarea data-testid="admin-guide-body" rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${inputCls} font-mono text-xs`} />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Risposta diretta (answer box 40-80 parole, subito sotto il titolo)</span>
            <textarea data-testid="admin-guide-direct-answer" rows={3} value={form.direct_answer} onChange={(e) => setForm({ ...form, direct_answer: e.target.value })} className={inputCls} />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">FAQ (una per riga: Domanda | Risposta)</span>
            <textarea data-testid="admin-guide-faq" rows={4} value={form.faq} onChange={(e) => setForm({ ...form, faq: e.target.value })} className={`${inputCls} font-mono text-xs`} />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Fonti (una per riga: Nome fonte | https://url)</span>
            <textarea data-testid="admin-guide-sources" rows={3} value={form.sources} onChange={(e) => setForm({ ...form, sources: e.target.value })} className={`${inputCls} font-mono text-xs`} />
          </label>
          <Field label="Guide correlate (slug separati da virgola)" data-testid="admin-guide-related" value={form.related} onChange={(e) => setForm({ ...form, related: e.target.value })} placeholder="patente-am, microcar" />
          <Field label="Ultimo aggiornamento (AAAA-MM-GG)" data-testid="admin-guide-updated" value={form.updated_at} onChange={(e) => setForm({ ...form, updated_at: e.target.value })} placeholder="2026-09-23" />
          <label className="block">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Intensità CTA</span>
            <select data-testid="admin-guide-cta" value={form.cta_level} onChange={(e) => setForm({ ...form, cta_level: e.target.value })} className={inputCls}>
              <option value="soft">Soft (informativa)</option>
              <option value="medium">Media (considerazione)</option>
              <option value="strong">Forte (commerciale)</option>
            </select>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-zinc-400">
            <input type="checkbox" data-testid="admin-guide-pillar" checked={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.checked })} className="h-4 w-4 accent-[#FFD700]" />
            Pillar page (in evidenza nell'hub)
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-zinc-400">
            <input type="checkbox" data-testid="admin-guide-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4 accent-[#FFD700]" />
            Pubblicata
          </label>
        </div>
        <div className="mt-5 flex gap-3">
          <button data-testid="admin-guide-save" onClick={save} className="bg-swapa-yellow px-5 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-black">
            {editing ? "Salva modifiche" : "Crea guida"}
          </button>
          {editing && <button onClick={() => { setEditing(null); setForm(blank); }} className="border border-white/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Annulla</button>}
        </div>
      </div>
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState({ refundable: "", cancellation: "", deduction: "", delivery: "", seller: "", terms_text: "" });
  useEffect(() => { api.get("/admin/settings").then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {}); }, []);

  const save = async () => {
    try {
      await api.put("/admin/settings", form);
      toast.success("Termini salvati");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const FIELDS = [
    ["refundable", "Rimborsabilità della caparra"],
    ["cancellation", "Condizioni di cancellazione"],
    ["deduction", "Detrazione dal prezzo finale"],
    ["delivery", "Consegna stimata"],
    ["seller", "Identità del venditore"],
    ["terms_text", "Termini contrattuali completi"],
  ];

  return (
    <div className="max-w-3xl" data-testid="admin-settings-form">
      <p className="border border-dashed border-zinc-700 p-4 font-mono text-[10px] uppercase tracking-[0.15em] leading-relaxed text-zinc-500">
        Questi testi regolano la caparra di €200 del preordine. Sostituisci i placeholder con
        i testi legali definitivi prima della pubblicazione.
      </p>
      <div className="mt-6 space-y-5">
        {FIELDS.map(([k, label]) => (
          <label key={k} className="block">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
            <textarea data-testid={`admin-settings-${k}`} rows={k === "terms_text" ? 5 : 2} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} />
          </label>
        ))}
      </div>
      <button data-testid="admin-settings-save" onClick={save} className="mt-6 bg-swapa-yellow px-6 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-black">
        Salva termini
      </button>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("panoramica");
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "SWAPA CMS";
    api.get("/auth/me")
      .then((r) => { setUser(r.data); setChecking(false); })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    if (user && tab === "panoramica") api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user, tab]);

  if (checking || !user) {
    return <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading"><p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Caricamento…</p></div>;
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink pb-20 pt-10">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div>
            <div className="flex items-center gap-3">
              <img src="/assets/logo-wordmark.webp" alt="SWAPA™" className="h-5 w-auto" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">CMS</span>
            </div>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{user.email}</p>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={async () => { await api.post("/auth/logout"); navigate("/admin/login"); }}
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 hover:border-red-400 hover:text-red-400"
          >
            <LogOut className="h-3.5 w-3.5" /> Esci
          </button>
        </div>

        <nav className="mt-6 flex flex-wrap gap-2" aria-label="Sezioni CMS">
          {TABS.map(([k, l]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                tab === k ? "border-swapa-yellow text-swapa-yellow" : "border-white/10 text-zinc-500 hover:border-white/30 hover:text-white"
              }`}
            >
              {l}
            </button>
          ))}
        </nav>

        <div className="mt-10">
          {tab === "panoramica" && stats && (
            <div className="grid grid-cols-2 gap-px bg-white/10 lg:grid-cols-6" data-testid="admin-stats">
              {[["Prenotazioni", stats.reservations], ["Pagate", stats.paid], ["Test drive", stats.leads], ["Centri", stats.centres], ["Colori", stats.colours], ["Guide", stats.guides]].map(([l, v]) => (
                <div key={l} className="bg-card p-6">
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{l}</p>
                  <p className="mt-2 font-display text-4xl font-bold text-white">{v}</p>
                </div>
              ))}
            </div>
          )}
          {tab === "colori" && <ColoursTab />}
          {tab === "centri" && <CentresTab />}
          {tab === "prenotazioni" && <ReservationsTab />}
          {tab === "recessi" && <WithdrawalsTab />}
          {tab === "finanza" && <FinanceTab />}
          {tab === "hospitality" && <HospitalityTab />}
          {tab === "testdrive" && <LeadsTab />}
          {tab === "guide" && <GuidesTab />}
          {tab === "termini" && <SettingsTab />}
        </div>
      </div>
    </div>
  );
}
